/**
 * CreaturePanel - Selected creature information
 * Displays detailed stats for the selected creature
 */

import { useSimulationStore } from '../stores/simulationStore';
import { CollapsiblePanel } from './ui';

interface CreaturePanelProps {
  onDeselect: () => void;
  onFollowToggle: () => void;
}

export function CreaturePanel({ onDeselect, onFollowToggle }: CreaturePanelProps) {
  const selectedCreature = useSimulationStore((state) => state.selectedCreature);
  const selectedCreatureId = useSimulationStore((state) => state.selectedCreatureId);
  const followCreature = useSimulationStore((state) => state.followCreature);

  // Also get basic data from creatures array for real-time position
  const creatures = useSimulationStore((state) => state.creatures);
  const basicData = creatures.find((c) => c.id === selectedCreatureId);

  if (!selectedCreatureId) {
    return (
      <CollapsiblePanel id="creature" title="Criatura Seleccionada">
        <p className="placeholder">Haz click en una criatura para inspeccionar</p>
      </CollapsiblePanel>
    );
  }

  const creature = selectedCreature;

  return (
    <CollapsiblePanel id="creature" title="Criatura Seleccionada">
      <div className="creature-header" style={{ marginBottom: 'var(--spacing-md)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
            }}
          >
            ID: {selectedCreatureId.slice(0, 8)}...
          </span>
          <button
            className="btn btn-secondary"
            onClick={onDeselect}
            style={{ flex: 'none', padding: 'var(--spacing-xs) var(--spacing-sm)' }}
          >
            Deseleccionar
          </button>
        </div>
      </div>

      {basicData && basicData.x !== undefined && (
        <>
          <div className="stats" style={{ marginBottom: 'var(--spacing-sm)' }}>
            <div className="stat">
              <span className="stat-label">Posicion X</span>
              <span className="stat-value">{basicData.x?.toFixed(0) ?? '-'}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Posicion Y</span>
              <span className="stat-value">{basicData.y?.toFixed(0) ?? '-'}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Energia</span>
              <span
                className="stat-value"
                style={{
                  color:
                    (basicData.energy ?? 0) / (basicData.maxEnergy ?? 1) > 0.3
                      ? 'var(--accent-primary)'
                      : 'var(--accent-danger)',
                }}
              >
                {basicData.energy?.toFixed(0) ?? '-'}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Energia Max</span>
              <span className="stat-value">{basicData.maxEnergy?.toFixed(0) ?? '-'}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Tamaño</span>
              <span className="stat-value">{basicData.size?.toFixed(1) ?? '-'}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Angulo</span>
              <span className="stat-value">
                {basicData.angle !== undefined ? (((basicData.angle * 180) / Math.PI).toFixed(0) + '°') : '-'}
              </span>
            </div>
          </div>

          {creature && (
            <div className="stats">
              <div className="stat">
                <span className="stat-label">Generacion</span>
                <span className="stat-value">{creature.generation}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Edad</span>
                <span className="stat-value">{creature.age}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Especie</span>
                <span className="stat-value">{creature.speciesId}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Aptitud</span>
                <span className="stat-value">{(creature.fitness ?? 0).toFixed(0)}</span>
              </div>
            </div>
          )}

          <button
            className={`btn ${followCreature ? 'btn-primary' : 'btn-secondary'}`}
            onClick={onFollowToggle}
            style={{ marginTop: 'var(--spacing-md)', width: '100%' }}
          >
            {followCreature ? 'Dejar de Seguir' : 'Seguir Criatura'}
          </button>
        </>
      )}

      {!basicData && (
        <p className="placeholder" style={{ color: 'var(--accent-danger)' }}>
          La criatura ya no existe
        </p>
      )}
    </CollapsiblePanel>
  );
}
